import { Component, ElementRef, EventEmitter, inject, Input, Output, TemplateRef, ViewChild } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { GlobalService, S3BucketService } from "../services";
import { Cls_MobiFile, FileType } from "../models";
import { BehaviorSubject, Observable } from "rxjs";
import { MatDialog } from "@angular/material/dialog";

@Component({
  selector: "mobi-office-lib-file-upload",
  templateUrl: "./file-upload.component.html",
  styleUrls: ["./file-upload.component.scss"],
  standalone: false,
})
export class FileUploadComponent {
  private readonly toastr = inject(ToastrService);
  public readonly globalService = inject(GlobalService);
  private readonly s3Bucket = inject(S3BucketService);
  public readonly dialog = inject(MatDialog);

  constructor() {}

  /**
   * Label of the file upload box
   */
  @Input() label: string = "";

  /**
   * Max file size in MB
   */
  @Input() Size: number = 2;

  @Input() isOnlyPreview: boolean = false; // Allow only document preview
  @Input() isMultipleUpload: boolean = true; // Allow multiple uploading / single upload
  @Input() isRequired: boolean = false; //All required mark on label
  @Input() FileType: string = "IMAGE"; // File Type : Image, PDF, Excel, Video, Audio, Doc, Text, Zip, PPT, SQL, JSON, XML, OTHER, RAR, GZ
  @Input() isBase64Want: boolean = false; // If will return base64 only and there is no preview allowed
  @Input() masterFiles: Cls_MobiFile[] = []; // Main Files
  @Input() allowDelete: boolean = true; // Allow only document delete
  @Input() allowDeleteFromServer: boolean = false; // Document will be delete from AWS server

  @Input() fileDeleteMessage: string = "File Deleted"; // File Upload card info message
  @Input() fileUploadInfoMessage: string = "Select & Upload File"; // File Upload card info message
  @Input() fileUploadSubInfoMessage: string = ""; // File Upload card info message
  @Input() singleFileUploadErrorMessage: string = "Delete existing file and try to upload again."; // Single file upload error if user try to upload morethan one file

  @Output() masterFilesChange = new EventEmitter();
  @Output() uploadFileEmitter = new EventEmitter();
  @Output() deleteFileEmitter = new EventEmitter();
  @ViewChild("fileInput") fileInput!: ElementRef;
  private selectedFileType: string = "IMAGE";
  FILE_TYPE = FileType;
  @ViewChild("fileContainsError") fileContainsError!: TemplateRef<any>;

  get fileTypes() {
    return this.FileType?.split(",")
      .map(el => el.toUpperCase())
      .join(", ");
  }

  //#region spinner

  private count: number = 0;
  private spinner$ = new BehaviorSubject<string>("");
  filesContainingError: string[] = [];
  getSpinnerObserver(): Observable<string> {
    return this.spinner$.asObservable();
  }

  requestStarted() {
    if (++this.count === 1) {
      this.spinner$.next("start");
    }
  }

  requestEnded() {
    if (this.count === 0 || --this.count === 0) {
      this.spinner$.next("stop");
    }
  }

  resetSpinner() {
    this.count = 0;
    this.spinner$.next("stop");
  }

  get showSpinner() {
    return this.spinner$.value === "start";
  }

  //#endregion

  onClickUploadFile() {
    if (!this.isMultipleUpload && this.masterFiles?.length) {
      this.toastr.info(this.singleFileUploadErrorMessage, "Info");
      return;
    }
    this.fileInput?.nativeElement?.click();
  }

  private validateFile(file: File): boolean {
    let isValid: boolean = true;
    const fileType: string = file.name;
    this.selectedFileType = "";

    this.selectedFileType = this.globalService.getFileType(file);

    isValid = !!this.FileType.split(",").find(el => el.toUpperCase()?.trim() === this.selectedFileType);

    if (!isValid) {
      if (this.isMultipleUpload) {
        this.filesContainingError.push(fileType);
        return false;
      }
      this.toastr.info("Invalid File Type", "Info");
      this.fileInput.nativeElement.value = "";
      return false;
    }

    if (file.size > this.Size * 1024 * 1024) {
      this.toastr.info(`File Size is too large. Max File Size is ${this.Size} MB`, "Info");
      this.fileInput.nativeElement.value = "";
      return false;
    }

    return isValid;
  }

  onFileChange(event: any) {
    this.filesContainingError = [];
    const files: File = event.target.files;
    Object.values(files).forEach((file: any) => {
      if (this.validateFile(file)) this.processFiles(file);
    });
    if (this.filesContainingError.length) this.onOpenPopup();
    this.fileInput.nativeElement.value = "";
  }

  onOpenPopup() {
    this.dialog.open(this.fileContainsError, {
      width: "500px",
      backdropClass: "panel-backdrop-class",
    });
  }

  processFiles(file: File) {
    // If want to upload directly on bucket
    if (!this.isBase64Want) {
      this.requestStarted();
      this.s3Bucket
        .uploadFile(file)
        ?.then((result: any) => {
          const { Location } = result;
          const tempFile = {
            fileName: file.name,
            fileType: this.globalService.getFileType(file),
            fileUrl: Location,
            fileSize: file.size,
          };
          this.masterFiles.push(tempFile);
          this.masterFilesChange.emit(this.masterFiles);
          this.uploadFileEmitter.emit(tempFile);
        })
        .catch(error => {
          this.toastr.error(error?.error?.message, "Error");
          console.error("File Upload Error : ", error);
        })
        .finally(() => {
          this.requestEnded();
        });
      return;
    }

    // If want base64 only
    const myReader: FileReader = new FileReader();
    myReader.readAsDataURL(file);
    myReader.onloadend = () => {
      const fileBase64 = myReader.result as string;
      const blob = new Blob([file], { type: file.type });
      const blobUrl = window.URL.createObjectURL(blob);
      this.masterFilesChange.emit({
        base64: fileBase64,
        type: file.type,
        name: file.name,
        size: file.size,
        blobUrl: blobUrl,
        file: file,
      });
    };
  }

  onDeleteDocument(item: Cls_MobiFile) {
    this.deleteFileEmitter.emit(item);
    if (!this.allowDeleteFromServer) {
      this.masterFiles = this.masterFiles.filter(el => el.fileName !== item.fileName);
      this.masterFilesChange.emit(this.masterFiles);
      return;
    }

    this.requestStarted();
    this.s3Bucket
      .deleteFile(item.fileName)
      ?.then(() => {
        this.toastr.success(this.fileDeleteMessage, "Success");
        this.masterFiles = this.masterFiles.filter(el => el.fileName !== item.fileName);
        this.masterFilesChange.emit(this.masterFiles);
      })
      .catch(error => {
        this.toastr.error(error?.error?.message, "Error");
        console.error("File Upload Error : ", error);
      })
      .then(() => {
        this.requestEnded();
      });
  }
}
